import { useCallback, useEffect, useRef, useState } from 'react';

import { millisecondsToMinutes } from 'date-fns';

import { StreamTypes } from 'store/player/actions.types';

import { VideoItem } from 'services/data-manager/types';
import useGetPlaybackMetricsParams from 'services/uav/useGetPlaybackMetricsParams';
import { logUavAnalytics } from 'services/uav/utils';

import usePrevious from 'utils/hooks/usePrevious';

import { StatisticsUpdateEvent } from 'components/Player/types.ts';

type Params = {
    playerProgress: number;
    videoItem: VideoItem | undefined;
    playbackType: StreamTypes | undefined;
};

function useOnStatisticsUpdate({
    playerProgress,
    videoItem,
    playbackType,
}: Params) {
    const [lastChannelChangeTime, setLastChannelChangeTime] = useState<
        undefined | number
    >();
    const [lastEventSentTime, setLastEventSentTime] = useState<
        undefined | number
    >();
    const { getPlaybackMetricsParams } = useGetPlaybackMetricsParams(
        playbackType,
        videoItem,
    );

    const prevChannelId = usePrevious(videoItem?.channelId);
    const progress = useRef(playerProgress);

    useEffect(() => {
        progress.current = playerProgress;
    }, [playerProgress]);

    const onStatisticsUpdate = useCallback<(e: StatisticsUpdateEvent) => void>(
        (e) => {
            const now = millisecondsToMinutes(Date.now());
            const shouldSendEventOnChannelChange =
                lastChannelChangeTime && now - lastChannelChangeTime >= 1;
            const shouldSendEventOnChannelWatching =
                lastEventSentTime && now - lastEventSentTime >= 3;

            if (
                shouldSendEventOnChannelChange ||
                shouldSendEventOnChannelWatching
            ) {
                setLastEventSentTime(millisecondsToMinutes(Date.now()));

                const payload = getPlaybackMetricsParams({
                    playbackProgress: progress.current,
                    uri: e.url,
                    droppedVideoFrames: e.droppedVideoFrames,
                    totalVideoFrames: e.totalVideoFrames,
                    selectedBitrate: e.selectedBitrate,
                    availableBitrates: e.availableBitrates,
                    downloadBitrate: e.downloadBitrate,
                    downloadBitrateAverage: e.downloadBitrateAverage,
                    bytesDownloaded: e.bytesDownloaded,
                    bufferedDuration: e.bufferedDuration,
                    streamBitrate:
                        e.streamBitrate != null
                            ? e.streamBitrate
                            : e.selectedBitrate,
                });

                setLastEventSentTime(millisecondsToMinutes(Date.now()));
                logUavAnalytics(payload);

                if (shouldSendEventOnChannelChange) {
                    setLastChannelChangeTime(undefined);
                }
            }
        },
        [lastChannelChangeTime, lastEventSentTime, getPlaybackMetricsParams],
    );

    useEffect(() => {
        if (prevChannelId !== videoItem?.channelId) {
            setLastEventSentTime(undefined);
            setLastChannelChangeTime(millisecondsToMinutes(Date.now()));
        }
    }, [prevChannelId, videoItem?.channelId]);

    return { onStatisticsUpdate };
}

export default useOnStatisticsUpdate;
